import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { buildApiUrl } from './config';
import HomeworkAssignmentSuccess from './HomeworkAssignmentSuccess';
import ClassroomSelector from './components/ClassroomSelector';

const GenerateHomeworkComponent = ({ availableForms = [], teacherName, editingHomework, onEditComplete }) => {
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [prompt, setPrompt] = useState('');
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [homeworkTitle, setHomeworkTitle] = useState('');
  const [subject, setSubject] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [savedDrafts, setSavedDrafts] = useState([]);
  const [showDrafts, setShowDrafts] = useState(false);
  
  // AI Generation Parameters
  const [yearLevel, setYearLevel] = useState('');
  const [aiSubject, setAiSubject] = useState('');
  const [topic, setTopic] = useState('');
  const [numQuestions, setNumQuestions] = useState(10);
  const [difficulty, setDifficulty] = useState('Medium');

  useEffect(() => {
    // Load saved drafts when component mounts
    loadSavedDrafts();
  }, []);

  // ✅ NEW: Load editing homework data
  useEffect(() => {
    if (editingHomework) {
      console.log('📝 Loading homework for editing in AI Generate:', editingHomework);
      
      // Populate form fields with existing homework data
      setHomeworkTitle(editingHomework.title || '');
      setSubject(editingHomework.subject || '');
      setDueDate(editingHomework.dueDate || '');
      
      // Handle both old format (single class) and new format (multiple classes)
      if (editingHomework.assignedTo) {
        if (Array.isArray(editingHomework.assignedTo)) {
          setSelectedClasses(editingHomework.assignedTo);
          setSelectedClass(editingHomework.assignedTo[0] || ''); // Fallback for compatibility
        } else {
          setSelectedClass(editingHomework.assignedTo);
          setSelectedClasses([editingHomework.assignedTo]);
        }
      }
      setPrompt(editingHomework.prompt || '');
      
      // Load questions if they exist
      if (editingHomework.questions && editingHomework.questions.length > 0) {
        setGeneratedQuestions(editingHomework.questions);
        setStep(2); // Go to review step
      } else {
        setStep(1); // Start at creation step
      }
    }
  }, [editingHomework]);

  // ✅ ADD: Check for draft data passed from navigation state
  useEffect(() => {
    if (location.state?.loadFromDraft && location.state?.draftData) {
      const draftData = location.state.draftData;
      console.log('Loading draft data:', draftData);
      
      // Load the draft data into the form
      setPrompt(draftData.prompt || '');
      setGeneratedQuestions(draftData.questions || []);
      
      // Move to step 2 (review) since questions are already generated
      if (draftData.questions && draftData.questions.length > 0) {
        setStep(2);
      }
    }
  }, [location.state]);

  // ✅ ADD: Check for homework bank data passed from navigation state
  useEffect(() => {
    if (location.state?.loadFromBank && location.state?.homeworkData) {
      const bankData = location.state.homeworkData;
      console.log('Loading homework from bank:', bankData);
      
      // Load the homework data into the form
      setHomeworkTitle(bankData.title || '');
      setSubject(bankData.subject || '');
      setDueDate(''); // Clear due date for new assignment
      setPrompt(bankData.prompt || '');
      setGeneratedQuestions(bankData.questions || []);
      
      // Clear assigned classes since this is a new assignment
      setSelectedClasses([]);
      setSelectedClass('');
      
      // Move to step 2 (review) if questions exist, otherwise start at step 1
      if (bankData.questions && bankData.questions.length > 0) {
        setStep(2);
      } else {
        setStep(1);
      }
    }
  }, [location.state]);

  const loadSavedDrafts = async () => {
    try {
      const response = await fetch(buildApiUrl('/saved-homework-drafts'));
      if (response.ok) {
        const data = await response.json();
        // Ensure drafts is always an array
        const drafts = Array.isArray(data) ? data : (data.drafts || []);
        setSavedDrafts(drafts);
      } else {
        console.warn('Failed to load saved drafts:', response.status);
        setSavedDrafts([]);
      }
    } catch (error) {
      console.error('Error loading saved drafts:', error);
      setSavedDrafts([]);
    }
  };

  const saveForLater = async () => {
    try {
      const draftData = {
        prompt: prompt,
        questions: generatedQuestions,
        createdBy: 'teacher' // You can get this from auth context
      };

      const response = await fetch(buildApiUrl('/save-homework-draft'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(draftData),
      });

      if (response.ok) {
        alert('Draft saved successfully!');
        loadSavedDrafts(); // Refresh the drafts list
      } else {
        alert('Error saving draft');
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Error saving draft');
    }
  };

  const loadDraft = (draft) => {
    setPrompt(draft.prompt);
    setGeneratedQuestions(draft.questions);
    setStep(2);
    setShowDrafts(false);
  };

  const deleteDraft = async (draftId) => {
    try {
      const response = await fetch(buildApiUrl(`/saved-homework-draft/${draftId}`), {
        method: 'DELETE',
      });

      if (response.ok) {
        alert('Draft deleted successfully!');
        loadSavedDrafts(); // Refresh the drafts list
      } else {
        alert('Error deleting draft');
      }
    } catch (error) {
      console.error('Error deleting draft:', error);
      alert('Error deleting draft');
    }
  };

  const generateHomework = async () => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('prompt', prompt || '');
      formData.append('year_level', yearLevel || '');
      formData.append('subject', aiSubject || '');
      formData.append('topic', topic || '');
      formData.append('num_questions', numQuestions.toString());
      formData.append('difficulty', difficulty || 'Medium');
      
      console.log('Sending homework generation request with:', {
        prompt: prompt || '',
        year_level: yearLevel || '',
        subject: aiSubject || '',
        topic: topic || '',
        num_questions: numQuestions,
        difficulty: difficulty || 'Medium'
      });
      
      const response = await fetch(buildApiUrl('/generate-homework-ai'), {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setGeneratedQuestions(data.questions);
        setStep(2);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error response:', errorData);
        alert(`Error generating homework: ${errorData.error || errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error generating homework:', error);
      alert('Error generating homework');
    } finally {
      setIsLoading(false);
    }
  };

  const submitHomework = async () => {
    try {
      const homeworkData = {
        title: homeworkTitle,
        subject: subject || 'Custom', // Use selected subject
        due_date: dueDate,
        questions: generatedQuestions,
        assignedTo: selectedClasses.length > 0 ? selectedClasses : [selectedClass], // Backend expects 'assignedTo' array with form names
        visible: true, // Make homework visible to students
        createdBy: teacherName || 'Teacher', // Track who created it
      };

      // Use different endpoint and method for editing vs creating
      const endpoint = editingHomework 
        ? `/homework/update/${editingHomework.id}` 
        : '/assign-homework-per-student';
      const method = editingHomework ? 'PUT' : 'POST';

      const response = await fetch(buildApiUrl(endpoint), {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(homeworkData),
      });

      if (response.ok) {
        const result = await response.json();
        const action = editingHomework ? 'updated' : 'assigned';
        alert(`Homework ${action} successfully! ${result.message || ''}`);
        
        if (editingHomework && onEditComplete) {
          onEditComplete(); // Navigate back to dashboard
        } else {
          setStep(4);
        }
      } else {
        const errorData = await response.json();
        console.error('Server error:', errorData);
        alert(`Error ${editingHomework ? 'updating' : 'submitting'} homework: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error submitting homework:', error);
      alert(`Error ${editingHomework ? 'updating' : 'submitting'} homework: ${error.message}`);
    }
  };

  const handleNext = () => {
    if (step === 2) {
      setStep(3);
    } else if (step === 3) {
      submitHomework();
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const resetProcess = () => {
    setStep(1);
    setPrompt('');
    setGeneratedQuestions([]);
    setHomeworkTitle('');
    setSubject('');
    setDueDate('');
    setSelectedClass('');
    setSelectedClasses([]);
    setSelectedStudents([]);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-6 text-center text-blue-600">Generate AI Homework</h1>
      
      {/* Progress indicator */}
      <div className="mb-8">
        <div className="flex justify-center items-center space-x-4">
          {[1, 2, 3, 4].map((num) => (
            <div key={num} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold ${
                  step >= num ? 'bg-blue-500' : 'bg-gray-300'
                }`}
              >
                {num}
              </div>
              {num < 4 && (
                <div className={`w-16 h-1 ${step > num ? 'bg-blue-500' : 'bg-gray-300'}`} />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-center mt-2 space-x-12 text-sm text-gray-600">
          <span>Prompt</span>
          <span>Review</span>
          <span>Assign</span>
          <span>Complete</span>
        </div>
      </div>

      {/* Saved Drafts Button - Always visible */}
      <div className="mb-4 text-center">
        <button
          onClick={() => setShowDrafts(!showDrafts)}
          className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors"
        >
          {showDrafts ? 'Hide Saved Drafts' : 'Show Saved Drafts'} ({Array.isArray(savedDrafts) ? savedDrafts.length : 0})
        </button>
      </div>

      {/* Saved Drafts List */}
      {showDrafts && (
        <div className="mb-6 bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold mb-3">Saved Drafts</h3>
          {!Array.isArray(savedDrafts) || savedDrafts.length === 0 ? (
            <p className="text-gray-500">No saved drafts found.</p>
          ) : (
            <div className="space-y-2">
              {savedDrafts.map((draft) => (
                <div key={draft.id} className="bg-white p-3 rounded border flex justify-between items-center">
                  <div>
                    <p className="font-medium">Draft from {new Date(draft.createdAt).toLocaleDateString()}</p>
                    <p className="text-sm text-gray-600 truncate max-w-md">{draft.prompt}</p>
                  </div>
                  <div className="space-x-2">
                    <button
                      onClick={() => loadDraft(draft)}
                      className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600 transition-colors"
                    >
                      Load
                    </button>
                    <button
                      onClick={() => deleteDraft(draft.id)}
                      className="bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 1: Prompt Input */}
      {step === 1 && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4">Step 1: Configure Homework Generation</h2>
          
          {/* AI Generation Parameters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Year Level <span className="text-red-500">*</span>
              </label>
              <select
                value={yearLevel}
                onChange={(e) => setYearLevel(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select Year Level</option>
                <option value="Pre-School">Pre-School</option>
                <option value="Reception">Reception</option>
                <option value="Year 1">Year 1</option>
                <option value="Year 2">Year 2</option>
                <option value="Year 3">Year 3</option>
                <option value="Year 4">Year 4</option>
                <option value="Year 5">Year 5</option>
                <option value="Year 6">Year 6</option>
                <option value="Year 7">Year 7</option>
                <option value="Year 8">Year 8</option>
                <option value="Year 9">Year 9</option>
                <option value="Year 10">Year 10</option>
                <option value="Year 11">Year 11</option>
                <option value="Year 12">Year 12</option>
                <option value="Year 13">Year 13</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject <span className="text-red-500">*</span>
              </label>
              <select
                value={aiSubject}
                onChange={(e) => setAiSubject(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select Subject</option>
                <option value="Mathematics">Mathematics</option>
                <option value="English">English</option>
                <option value="Science">Science</option>
                <option value="Biology">Biology</option>
                <option value="Chemistry">Chemistry</option>
                <option value="Physics">Physics</option>
                <option value="History">History</option>
                <option value="Geography">Geography</option>
                <option value="Arabic">Arabic</option>
                <option value="Islamic Studies">Islamic Studies</option>
                <option value="Computer Science">Computer Science</option>
                <option value="Art">Art</option>
                <option value="Music">Music</option>
                <option value="Physical Education">Physical Education</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Topic <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g., Fractions, Photosynthesis, Shakespeare"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Number of Questions <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={numQuestions}
                onChange={(e) => setNumQuestions(Math.max(1, Math.min(20, parseInt(e.target.value) || 10)))}
                min="1"
                max="20"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">Choose between 1-20 questions</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Difficulty Level <span className="text-red-500">*</span>
              </label>
              <select
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="Easy">Easy</option>
                <option value="Medium">Medium</option>
                <option value="Hard">Hard</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Adjust question complexity</p>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Instructions (Optional)
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Give specific instructions and the AI will follow them exactly. Examples:&#10;• '3 digit + 3 digit addition' → generates only questions like 432 + 617&#10;• '2 digit x 1 digit multiplication' → generates only questions like 47 x 6&#10;• 'Word problems involving fractions of amounts'&#10;• 'Questions about the water cycle with diagrams described'&#10;• 'Focus on persuasive writing techniques'"
              className="w-full p-4 border border-gray-300 rounded-lg h-28 resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div className="mt-4 flex justify-end">
            <button
              onClick={generateHomework}
              disabled={!yearLevel || !aiSubject || !topic || isLoading}
              className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Generating...' : 'Generate Homework'}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Review Generated Questions */}
      {step === 2 && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Step 2: Review Generated Questions</h2>
            <button
              onClick={saveForLater}
              className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition-colors"
            >
              Save for Later
            </button>
          </div>
          
          <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="font-semibold text-gray-800 mb-2">📋 Generation Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              {yearLevel && (
                <div><span className="text-gray-500">Year:</span> <span className="font-medium">{yearLevel}</span></div>
              )}
              {aiSubject && (
                <div><span className="text-gray-500">Subject:</span> <span className="font-medium">{aiSubject}</span></div>
              )}
              {topic && (
                <div><span className="text-gray-500">Topic:</span> <span className="font-medium">{topic}</span></div>
              )}
              <div><span className="text-gray-500">Difficulty:</span> <span className="font-medium">{difficulty}</span></div>
              <div><span className="text-gray-500">Questions:</span> <span className="font-medium">{generatedQuestions.length}</span></div>
            </div>
            {prompt && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <span className="text-gray-500 text-sm">Teacher Instructions:</span>
                <p className="font-medium text-gray-800 mt-1">{prompt}</p>
              </div>
            )}
          </div>
          
          {/* Teacher Question Review - NOT student view */}
          <div className="space-y-4 mb-6">
            {generatedQuestions.map((q, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-6 bg-white">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Question {index + 1}</h3>
                  <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    {q.type === "single" ? "Single Choice" : q.type === "multiple" ? "Multiple Choice" : "Text Answer"}
                  </span>
                </div>
                
                <div className="mb-4">
                  <p className="text-gray-900 mb-2">{q.question || q.label}</p>
                  {q.image && (
                    <img src={q.image} alt="Question" className="max-w-md rounded-lg mb-2" />
                  )}
                </div>

                {q.type === "text" ? (
                  <div>
                    <div className="bg-gray-50 p-3 rounded border mb-2">
                      <p className="text-sm text-gray-700 italic">Student will type their answer here...</p>
                    </div>
                    <p className="text-sm text-green-600">
                      <strong>Expected Answer:</strong> {q.correct}
                    </p>
                  </div>
                ) : (
                  <div>
                    <div className="space-y-2 mb-2">
                      {q.options?.map((option, optIndex) => (
                        <div key={optIndex} className="flex items-center gap-2">
                          <input
                            type={q.type === "single" ? "radio" : "checkbox"}
                            name={`preview-question-${index}`}
                            disabled
                            checked={option === q.correct}
                            className="text-blue-600"
                          />
                          <span className={`${option === q.correct ? 'text-green-600 font-medium' : 'text-gray-700'}`}>
                            {option}
                            {option === q.correct && " ✓ (Correct)"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className="mt-6 flex justify-between">
            <button
              onClick={handleBack}
              className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors"
            >
              Back
            </button>
            <button
              onClick={handleNext}
              className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors"
            >
              {editingHomework ? 'Next: Update Homework' : 'Next: Assign Homework'}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Assignment Details */}
      {step === 3 && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4">Step 3: Assignment Details</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Homework Title
              </label>
              <input
                type="text"
                value={homeworkTitle}
                onChange={(e) => setHomeworkTitle(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter homework title"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subject
              </label>
              <select
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select a subject</option>
                <option value="Math">Math</option>
                <option value="Science">Science</option>
                <option value="Geography">Geography</option>
                <option value="English">English</option>
                <option value="Literacy">Literacy</option>
                <option value="ICT">ICT</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due Date
              </label>
              <input
                type="datetime-local"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Classes
              </label>
              <ClassroomSelector
                teacherName={teacherName}
                selectedClasses={selectedClasses}
                onSelectionChange={setSelectedClasses}
                assignedForms={availableForms}
              />
            </div>
          </div>
          
          <div className="mt-6 flex justify-between">
            <button
              onClick={handleBack}
              className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors"
            >
              Back
            </button>
            <button
              onClick={handleNext}
              disabled={!homeworkTitle || !subject || !dueDate || (selectedClasses.length === 0 && !selectedClass)}
              className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {editingHomework ? 'Update Homework' : 'Assign Homework'}
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Success */}
      {step === 4 && (
        <HomeworkAssignmentSuccess
          title={homeworkTitle}
          dueDate={dueDate}
          className={selectedClasses.length > 0 ? selectedClasses.join(', ') : selectedClass}
          questions={generatedQuestions}
          selectedForms={selectedClasses.length > 0 ? selectedClasses : [selectedClass].filter(Boolean)}
          onReset={resetProcess}
        />
      )}
    </div>
  );
};

export default GenerateHomeworkComponent;
